import { Component, Element, h, Host, Listen, Prop, State } from "@stencil/core";

import { getPlaceholderUrl } from "../../utils/getPlaceholderUrl";

const elementClass = "ux-checkbox-tile";

/**
 * @slot (default) - Used for the ux-checkbox component to be wrapped
 * @slot description - Used to insert a description for the checkbox,
 *                     which can be toggle by clicking on the info icon
 */
@Component({
    tag: "ux-checkbox-tile",
    styleUrl: "checkbox-tile.scss",
    shadow: false
})
export class Checkbox {
    /**
     * the image url for the media element of the checkbox
     */
    @Prop() readonly imgSrc: string = "";
    /**
     * optional text of the tag
     */
    @Prop() readonly tag: string = "";
    /**
     * color of the tag
     */
    @Prop() readonly tagColor: "warning" | "info" = "info";
    /**
     * optional lazyloading of images
     */
    @Prop() readonly lazyload: boolean = false;
    /**
     * alt image tag
     */
    @Prop() readonly imageAlt: string = "";

    @State() checkboxChecked = false;
    @State() hasDescription = false;
    @State() descriptionActive = false;

    @Element() element!: HTMLUxCheckboxTileElement;
    private checkboxInputElement: HTMLInputElement;
    private checkboxElement: HTMLUxCheckboxElement;
    private descriptionContainer: HTMLDivElement;
    private descriptionToggle: HTMLButtonElement;

    componentWillLoad() {
        this.hasDescription = !!this.element.querySelector('[slot="description"]');
    }

    componentDidLoad() {
        this.checkboxInputElement = this.element.querySelector("input[type=checkbox]") as HTMLInputElement;
        // @ts-expect-error Check for typing
        this.checkboxElement = this.element.querySelector("ux-checkbox");
        if (this.checkboxInputElement?.checked) {
            this.checkboxChecked = true;
        }
    }

    /** checkbox event, includes checked status, inputId and input name */
    @Listen("uxCheckboxChange", { target: "window" })
    handleActiveState(e: CustomEvent) {
        if (this.element.contains(e.target as HTMLElement)) {
            if (e.detail.checked) {
                this.checkboxChecked = true;
            } else {
                this.checkboxChecked = false;
            }
        }
    }

    private handleDescription = () => {
        this.descriptionActive = !this.descriptionActive;
    };

    private onElementClick = (e: Event) => {
        if (e.target === this.descriptionContainer) {
            return;
        }
        if ((e.target as HTMLElement).closest(`.${elementClass}__description--active`)) {
            return;
        }
        if (e.target === this.descriptionToggle) {
            return;
        }
        if ((e.target as HTMLElement).closest(`.${elementClass}__description-toggle`)) {
            return;
        }
        if (e.target === this.checkboxElement) {
            return;
        }
        if ((e.target as HTMLElement).closest("ux-checkbox")) {
            return;
        }
        this.checkboxInputElement.click();
    };

    render() {
        return (
            <Host
                class={{
                    [elementClass]: true,
                    [`${elementClass}--active`]: this.checkboxChecked
                }}
                onClick={this.onElementClick}
            >
                <div class={`${elementClass}__top-wrapper`}>
                    {this.lazyload && (
                        <picture>
                            <img class="lazyload" src={getPlaceholderUrl(1024, 576)} data-src={this.imgSrc} alt={this.imageAlt} />
                        </picture>
                    )}
                    {!this.lazyload && (
                        <picture>
                            <img src={this.imgSrc} alt={this.imageAlt} />
                        </picture>
                    )}

                    {this.hasDescription && (
                        <div
                            class={{
                                [`${elementClass}__description`]: true,
                                [`${elementClass}__description--active`]: this.descriptionActive
                            }}
                            aria-hidden={this.descriptionActive ? "false" : "true"}
                            // @ts-expect-error Check for typing
                            ref={(el) => (this.descriptionContainer = el)}
                        >
                            <ux-icon
                                name="cross"
                                weight="normal"
                                class={`${elementClass}__description-close`}
                                onClick={this.handleDescription}
                                onKeyDown={this.handleDescription}
                                tabindex="0"
                                size-s="13"
                            ></ux-icon>
                            <slot name="description" />
                        </div>
                    )}
                </div>
                <div class={`${elementClass}__bottom-wrapper`}>
                    {this.tag !== "" && <div class={`${elementClass}__tag ${elementClass}__tag--${this.tagColor}`}>{this.tag}</div>}
                    <div class={`${elementClass}__checkbox-container`}>
                        <slot />
                    </div>
                    {this.hasDescription && (
                        <button
                            class={`${elementClass}__description-toggle`}
                            onClick={this.handleDescription}
                            type="button"
                            // @ts-expect-error Check for typing
                            ref={(el) => (this.descriptionToggle = el)}
                        >
                            <ux-icon name="square-info" weight="bold" size-s="15"></ux-icon>
                        </button>
                    )}
                </div>
            </Host>
        );
    }
}
